import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { GlowFilter } from 'pixi-filters';
import { createNoise3D, type NoiseFunction3D } from 'simplex-noise';
import seedrandom from 'seedrandom';
import { assetLoader } from '../core/AssetLoader';

// Sun data from server
export interface SunData {
  id: string;
  name: string;
  x: number;
  y: number;
  radius: number;
  starType: string;
  damageRadius: number;
  baseDamagePerSecond: number;
  sunSeed?: number;
}

// Star type visual config
interface StarStyle {
  /** Core colour (hottest center region). */
  core: [number, number, number];
  /** Mid-body colour. */
  mid: [number, number, number];
  /** Edge / limb colour. */
  edge: [number, number, number];
  /** Hot-spot / flare accent. */
  flare: [number, number, number];
  /** PixiJS glow filter colour. */
  glowColor: number;
  /** Name label colour. */
  labelColor: number;
}

const STAR_STYLES: Record<string, StarStyle> = {
  red_dwarf: {
    core:  [255, 210, 160],
    mid:   [220, 80, 30],
    edge:  [160, 30, 10],
    flare: [255, 120, 40],
    glowColor: 0xff4422,
    labelColor: 0xff6644,
  },
  yellow: {
    core:  [255, 255, 235],
    mid:   [255, 220, 80],
    edge:  [240, 170, 40],
    flare: [255, 240, 180],
    glowColor: 0xffcc33,
    labelColor: 0xffdd44,
  },
  blue_giant: {
    core:  [235, 245, 255],
    mid:   [100, 160, 255],
    edge:  [40, 80, 200],
    flare: [180, 210, 255],
    glowColor: 0x4488ff,
    labelColor: 0x66aaff,
  },
  neutron: {
    core:  [245, 235, 255],
    mid:   [180, 120, 255],
    edge:  [120, 60, 200],
    flare: [220, 180, 255],
    glowColor: 0xbb55ff,
    labelColor: 0xdd66ff,
  },
};

function getStarStyle(starType: string): StarStyle {
  return STAR_STYLES[starType] ?? STAR_STYLES.yellow;
}

// ==================== PROCEDURAL SUN TEXTURE (simplex noise) ====================

const sunTextureCache: Map<string, Texture> = new Map();

/** Derive a deterministic seed from sun ID if DB seed is missing. */
function seedFromId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) || 1;
}

function lerp3(
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

/**
 * Map a pixel (px,py) in the square texture to a point on the unit sphere.
 * This gives the noise a seamless, spherical look — no tiling artifacts.
 */
function pixelToSphere(px: number, py: number, size: number) {
  const lat = 90 - (py / size) * 180;          // +90 top → -90 bottom
  const lon = -180 + (px / size) * 360;         // wraps around
  const phi = (lat * Math.PI) / 180;
  const lam = (lon * Math.PI) / 180;
  const cp = Math.cos(phi);
  return { x: cp * Math.cos(lam), y: cp * Math.sin(lam), z: Math.sin(phi) };
}

/**
 * Generate a procedural sun texture using simplex noise for solar-surface
 * granulation, convection cells, and bright flares.
 */
function bakeSunTexture(
  starType: string,
  sunSeed: number,
  diameter: number,
): Texture {
  const key = `${starType}-${sunSeed}-${diameter}`;
  const cached = sunTextureCache.get(key);
  if (cached) return cached;

  const style = getStarStyle(starType);
  const size = Math.min(diameter, 512);
  const half = size / 2;
  const rMax = half;

  // Create seeded noise functions — three octaves like planet terrain
  const rng = seedrandom(`sun:${sunSeed}`);
  const noise1: NoiseFunction3D = createNoise3D(() => rng());
  const noise2: NoiseFunction3D = createNoise3D(() => rng());
  const noise3: NoiseFunction3D = createNoise3D(() => rng());

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const imgData = ctx.createImageData(size, size);
  const d = imgData.data;

  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      const dx = px - half;
      const dy = py - half;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const idx = (py * size + px) * 4;

      if (dist > rMax) {
        d[idx] = d[idx + 1] = d[idx + 2] = d[idx + 3] = 0;
        continue;
      }

      // Normalized radial position [0=center, 1=edge]
      const r = dist / rMax;

      // Sample noise on a sphere for seamless wrapping
      const { x: sx, y: sy, z: sz } = pixelToSphere(px, py, size);

      // Octave 1: large convection cells (low freq)
      const n1 = noise1(sx * 2.5, sy * 2.5, sz * 2.5) * 0.50;
      // Octave 2: granulation (mid freq)
      const n2 = noise2(sx * 6, sy * 6, sz * 6) * 0.30;
      // Octave 3: fine surface detail (high freq)
      const n3 = noise3(sx * 14, sy * 14, sz * 14) * 0.20;

      // Combined noise in [-1, 1] range, normalized to [0, 1]
      const noiseVal = (n1 + n2 + n3 + 1) * 0.5;

      // Base radial colour: core → mid → edge
      let baseColor: [number, number, number];
      if (r < 0.3) {
        baseColor = lerp3(style.core, style.mid, r / 0.3);
      } else {
        baseColor = lerp3(style.mid, style.edge, (r - 0.3) / 0.7);
      }

      // Blend in flare colour at noise peaks (bright spots = hot convection)
      const flareStrength = Math.max(0, (noiseVal - 0.55) * 3.0); // 0 at 0.55, 1+ above
      const blended = lerp3(baseColor, style.flare, Math.min(flareStrength, 0.6));

      // Noise modulates brightness — stronger toward mid-radius
      const noiseWeight = 0.30 * Math.sin(r * Math.PI); // peaks at r=0.5
      const brightness = 1 + (noiseVal - 0.5) * 2 * noiseWeight;

      let cr = blended[0] * brightness;
      let cg = blended[1] * brightness;
      let cb = blended[2] * brightness;

      // Limb darkening — darken edges smoothly
      const limb = 1 - Math.pow(r, 2.2) * 0.45;
      cr *= limb;
      cg *= limb;
      cb *= limb;

      // Anti-alias at the very edge
      let alpha = 255;
      if (dist > rMax - 1.5) {
        alpha = Math.max(0, Math.round((rMax - dist) / 1.5 * 255));
      }

      d[idx]     = Math.min(255, Math.round(cr));
      d[idx + 1] = Math.min(255, Math.round(cg));
      d[idx + 2] = Math.min(255, Math.round(cb));
      d[idx + 3] = alpha;
    }
  }

  ctx.putImageData(imgData, 0, 0);

  // Hot white corona bloom at center
  const coronaGrad = ctx.createRadialGradient(half, half, 0, half, half, half * 0.55);
  coronaGrad.addColorStop(0, 'rgba(255, 255, 255, 0.30)');
  coronaGrad.addColorStop(0.25, 'rgba(255, 255, 255, 0.08)');
  coronaGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = coronaGrad;
  ctx.beginPath();
  ctx.arc(half, half, half, 0, Math.PI * 2);
  ctx.fill();

  const texture = Texture.from(canvas);
  sunTextureCache.set(key, texture);
  return texture;
}

// Cache for zone textures (per radius)
const zoneTextureCache: Map<string, Texture> = new Map();

export class Sun extends Container {
  public sunId: string;
  public sunName: string;
  public starType: string;
  public sunRadius: number;
  public damageRadius: number;
  public baseDamagePerSecond: number;

  private zoneSprite: Sprite;
  private glowGraphics: Graphics;
  private sunSprite: Sprite;
  private nameText: Text;

  constructor(data: SunData) {
    super();

    this.position.set(data.x, data.y);
    this.sunId = data.id;
    this.sunName = data.name;
    this.starType = data.starType;
    this.sunRadius = data.radius;
    this.damageRadius = data.damageRadius;
    this.baseDamagePerSecond = data.baseDamagePerSecond;

    this.sortableChildren = true;

    // Zone overlay (radiation boundary)
    const zoneTexture = this.getOrCreateZoneTexture();
    this.zoneSprite = new Sprite(zoneTexture);
    this.zoneSprite.anchor.set(0.5);
    this.zoneSprite.zIndex = -2;
    this.addChild(this.zoneSprite);

    // White glow layer behind the sun body
    this.glowGraphics = new Graphics();
    this.glowGraphics.zIndex = -1;
    this.glowGraphics.circle(0, 0, this.sunRadius * 1.15);
    this.glowGraphics.fill({ color: 0xffffff, alpha: 0.1 });
    this.addChild(this.glowGraphics);

    // Main sun sprite (procedural with simplex noise)
    const style = getStarStyle(this.starType);
    const seed = (data.sunSeed && data.sunSeed > 0) ? data.sunSeed : seedFromId(data.id);
    const diameter = Math.round(this.sunRadius * 2);
    const texture = bakeSunTexture(this.starType, seed, diameter);

    this.sunSprite = new Sprite(texture);
    this.sunSprite.anchor.set(0.5);
    this.sunSprite.zIndex = 0;
    const texSize = Math.max(texture.width, texture.height);
    this.sunSprite.scale.set((this.sunRadius * 2) / texSize);
    this.sunSprite.filters = [new GlowFilter({
      distance: 20,
      outerStrength: 0.8,
      innerStrength: 0.3,
      color: style.glowColor,
      quality: 0.3,
    })];
    this.addChild(this.sunSprite);

    // Name label
    this.nameText = new Text({
      text: this.sunName.toUpperCase(),
      style: {
        fontFamily: 'monospace',
        fontSize: 16,
        fill: style.labelColor,
        stroke: { color: 0x0a1628, width: 3 },
      },
    });
    this.nameText.anchor.set(0.5);
    this.nameText.position.set(0, -this.sunRadius - 80);
    this.nameText.zIndex = 2;
    this.addChild(this.nameText);
  }

  // Zone texture (radiation boundary markers)
  private getOrCreateZoneTexture(): Texture {
    const key = `sun-zones-${this.damageRadius}`;
    if (zoneTextureCache.has(key)) return zoneTextureCache.get(key)!;
    const texture = this.generateZoneTexture(key);
    zoneTextureCache.set(key, texture);
    return texture;
  }

  private generateZoneTexture(key: string): Texture {
    const graphics = new Graphics();
    const size = this.damageRadius * 2 + 40;
    const center = size / 2;

    // Dashed red circle
    const circumference = 2 * Math.PI * this.damageRadius;
    const dashLength = 30, gapLength = 15;
    const totalSegments = Math.floor(circumference / (dashLength + gapLength));
    const anglePerSegment = (2 * Math.PI) / totalSegments;
    const dashAngle = (dashLength / circumference) * 2 * Math.PI;

    for (let i = 0; i < totalSegments; i++) {
      const startAngle = i * anglePerSegment;
      graphics.arc(center, center, this.damageRadius, startAngle, startAngle + dashAngle);
      graphics.stroke({ width: 2, color: 0xff0000, alpha: 0.25 });
    }

    // Faint fill
    graphics.circle(center, center, this.damageRadius);
    graphics.fill({ color: 0xff0000, alpha: 0.04 });

    // Warning triangles
    const numMarkers = 16;
    for (let i = 0; i < numMarkers; i++) {
      const angle = (i / numMarkers) * Math.PI * 2;
      const x = center + Math.cos(angle) * this.damageRadius;
      const y = center + Math.sin(angle) * this.damageRadius;
      const ms = 12;
      const tipX = x + Math.cos(angle) * ms;
      const tipY = y + Math.sin(angle) * ms;
      const perp = angle + Math.PI / 2;
      graphics.poly([tipX, tipY, x + Math.cos(perp) * ms * 0.5, y + Math.sin(perp) * ms * 0.5, x - Math.cos(perp) * ms * 0.5, y - Math.sin(perp) * ms * 0.5], true);
      graphics.fill({ color: 0xff0000, alpha: 0.35 });
    }

    const texture = assetLoader.generateTexture(graphics, key);
    graphics.destroy();
    return texture;
  }

  isInDangerZone(x: number, y: number): boolean {
    const dx = x - this.x, dy = y - this.y;
    return Math.sqrt(dx * dx + dy * dy) < this.damageRadius;
  }

  getDistanceTo(x: number, y: number): number {
    const dx = x - this.x, dy = y - this.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  getRadiationProximity(x: number, y: number): number {
    const distance = this.getDistanceTo(x, y);
    if (distance >= this.damageRadius) return 0;
    return 1 - (distance / this.damageRadius);
  }

  update(_delta: number): void {
    // Static -- no animation
  }

  updateFromServer(data: Partial<SunData>): void {
    if (data.name !== undefined) {
      this.sunName = data.name;
      this.nameText.text = data.name.toUpperCase();
    }
  }

  public getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  destroy(): void {
    this.sunSprite.destroy();
    this.zoneSprite.destroy();
    this.glowGraphics.destroy();
    this.nameText.destroy();
    super.destroy();
  }
}
